import { chunk, first, isEmpty, last, noop } from 'lodash';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import { useKeypress } from '@/hooks/useKeypress';
import { isBeneficiaryDesignationEntity } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { useEntityBalanceSheetPages } from '@/modules/presentation/clientPresentation/components/slides/EntityBalanceSheet/EntityBalanceSheetSlide';
import { EntityBeneficiariesAndDispositionsSlideProps } from '@/modules/presentation/slides/EntityBeneficiariesAndDispositionsSlide/EntityBeneficiariesAndDispositionsSlide';
import { useEntityGrantors } from '@/modules/presentation/slides/EntityBeneficiariesAndDispositionsSlide/EntityBeneficiariesAndDispositionsSlide.hooks';
import {
  getInsurancePolicies,
  getSummaryPropsList,
} from '@/modules/presentation/slides/EntityBeneficiariesAndDispositionsSlide/EntityBeneficiariesAndDispositionsSlide.utils';
import { diagnostics } from '@/utils/diagnostics';

import { EntityDetail_InsurancePolicyFragment } from '../../details/graphql/EntityDetailPage.generated';
import { EntitySlideType } from '../ExportEntityBundleModal/ExportEntityBundleModal.types';
import { EntityPreviewModalFormShape } from './EntityPreviewModal.types';
import { EntityPreviewModal_EntityFragment } from './graphql/EntityPreviewModal.generated';

interface PaginationShape {
  /** Which current type of slide to display */
  currentSlideType: EntitySlideType;
  /**
   * If the slide type has multiple pages, which of them to show.
   * Applies to BeneficiariesAndDispositionsSlide and InsurancePolicySlides types.
   */
  currentSlideSubIndex: number;
  previousSlideEnabled: boolean;
  nextSlideEnabled: boolean;
}

interface InsuranceShape {
  policyPages: EntityDetail_InsurancePolicyFragment[][];
  numberOfPolicySlides: number;
  hasInsurance: boolean;
}

interface BeneficiariesAndDispositionsShape {
  slidesProps: Omit<EntityBeneficiariesAndDispositionsSlideProps, 'slideId'>[];
  numberOfBeneficiariesAndDispositionsSlides: number;
}

export interface EntityPreviewModalPaginationShape
  extends PaginationShape,
    InsuranceShape,
    BeneficiariesAndDispositionsShape {
  nextSlide: () => void;
  previousSlide: () => void;
}

type PerSlideTypePageCounts = {
  key: EntitySlideType;
  pageCount: number;
}[];

export const EntityPreviewModalPaginationContext =
  createContext<EntityPreviewModalPaginationShape>({
    currentSlideType: EntitySlideType.CoverSlide,
    currentSlideSubIndex: 0,
    nextSlide: noop,
    previousSlide: noop,
    nextSlideEnabled: true,
    previousSlideEnabled: true,
    policyPages: [],
    numberOfPolicySlides: 0,
    hasInsurance: false,
    slidesProps: [],
    numberOfBeneficiariesAndDispositionsSlides: 0,
  });

export interface EntityPreviewModalPaginationProviderProps
  extends PropsWithChildren {
  initialSlide?: EntitySlideType;
  entity: EntityPreviewModal_EntityFragment | null | undefined;
}

export function useGuardedEntityPreviewModalPaginationContext() {
  return useGuardedContext<EntityPreviewModalPaginationShape>(
    EntityPreviewModalPaginationContext,
    'EntityPreviewModalPaginationProvider'
  );
}

/**
 * Returns an array of slide types in the order they should be navigated, based on the
 * number of pages within each type.
 *
 * @param slidesPerPageType Map of slide types to the number of pages of that type
 * @returns Array of slide types in the order they should be navigated
 */
function getNavOrder(
  slidesPerPageType: PerSlideTypePageCounts
): EntitySlideType[] {
  return slidesPerPageType.reduce<EntitySlideType[]>(
    (acc, { key, pageCount }) => {
      if (pageCount) acc.push(key);
      return acc;
    },
    []
  );
}

/**
 * Returns whether the next slide is enabled, based on the current slide type and index,
 * and the number of pages within each type.
 *
 * @param pagination Current pagination state
 * @param pageCounts Map of slide types to the number of pages of that type
 * @returns Whether the next slide is enabled
 */
function isNextSlideEnabled(
  pagination: Pick<
    PaginationShape,
    'currentSlideType' | 'currentSlideSubIndex'
  >,
  pageCounts: PerSlideTypePageCounts
): boolean {
  const { currentSlideType, currentSlideSubIndex } = pagination;
  const navOrder = getNavOrder(pageCounts);
  const isLastType = currentSlideType === last(navOrder);

  if (!isLastType) return true;

  const slideTypeCount = pageCounts.find(
    ({ key }) => key === currentSlideType
  )?.pageCount;

  if (!slideTypeCount) {
    diagnostics.info(`Could not find slide type count for ${currentSlideType}`);
    return false;
  }

  return slideTypeCount > currentSlideSubIndex + 1;
}

/**
 * Returns whether the previous slide is enabled, based on the current slide type and index,
 * and the number of pages within each type.
 *
 * @param pagination Current pagination state
 * @param pageCounts Map of slide types to the number of pages of that type
 * @returns Whether the previous slide is enabled
 */
function isPreviousSlideEnabled(
  pagination: Pick<
    PaginationShape,
    'currentSlideType' | 'currentSlideSubIndex'
  >,
  pageCounts: PerSlideTypePageCounts
): boolean {
  const { currentSlideType, currentSlideSubIndex } = pagination;
  const navOrder = getNavOrder(pageCounts);
  const isFirstType = currentSlideType === first(navOrder);
  if (!isFirstType) return true;
  return currentSlideSubIndex > 0;
}

export function EntityPreviewModalPaginationProvider({
  initialSlide = EntitySlideType.CoverSlide,
  children,
  entity,
}: EntityPreviewModalPaginationProviderProps) {
  const { control } = useFormContext<EntityPreviewModalFormShape>();
  const [deathOrderId] = useWatch({
    control,
    name: ['deathOrderId'],
  });
  const grantors = useEntityGrantors(entity);
  const { pages: balanceSheetPages } = useEntityBalanceSheetPages(entity?.id);
  const useBeneficiaryDesignations = isBeneficiaryDesignationEntity(
    entity?.kind
  );

  const { policyPages, numberOfPolicySlides, hasInsurance } =
    useMemo<InsuranceShape>(() => {
      if (!entity) {
        return {
          policyPages: [],
          numberOfPolicySlides: 0,
          hasInsurance: false,
        };
      }
      const insurancePolicies = getInsurancePolicies(entity);
      const policyPages = chunk(insurancePolicies, 2);
      const numberOfPolicySlides = policyPages.length;
      return {
        policyPages,
        numberOfPolicySlides,
        hasInsurance: !isEmpty(insurancePolicies),
      };
    }, [entity]);

  const { slidesProps, numberOfBeneficiariesAndDispositionsSlides } =
    useMemo<BeneficiariesAndDispositionsShape>(() => {
      if (!entity) {
        return {
          slidesProps: [],
          numberOfBeneficiariesAndDispositionsSlides: 0,
        };
      }
      const slidesProps = getSummaryPropsList({
        entity,
        deathOrderId,
        grantors,
        useBeneficiaryDesignations,
      });
      return {
        slidesProps,
        numberOfBeneficiariesAndDispositionsSlides: slidesProps.length,
      };
    }, [deathOrderId, entity, grantors, useBeneficiaryDesignations]);

  const slidesPerPageType: PerSlideTypePageCounts = useMemo(
    () => [
      {
        key: EntitySlideType.CoverSlide,
        pageCount: 1,
      },
      {
        key: EntitySlideType.DiagramAndNotesSlide,
        pageCount: 1,
      },
      {
        key: EntitySlideType.BalanceSheetSlide,
        pageCount: balanceSheetPages.length,
      },
      // access and dispositions, insurance policies have variable page counts
      {
        key: EntitySlideType.BeneficiariesAndDispositionsSlide,
        pageCount: numberOfBeneficiariesAndDispositionsSlides,
      },
      {
        key: EntitySlideType.InsurancePolicySlides,
        pageCount: numberOfPolicySlides,
      },
    ],
    [
      numberOfBeneficiariesAndDispositionsSlides,
      numberOfPolicySlides,
      balanceSheetPages.length,
    ]
  );

  const [
    {
      currentSlideType,
      currentSlideSubIndex,
      previousSlideEnabled,
      nextSlideEnabled,
    },
    setPagination,
  ] = useState<PaginationShape>(() => {
    const payload = {
      currentSlideType: initialSlide,
      currentSlideSubIndex: 0,
    };
    return {
      ...payload,
      previousSlideEnabled: isPreviousSlideEnabled(payload, slidesPerPageType),
      nextSlideEnabled: isNextSlideEnabled(payload, slidesPerPageType),
    };
  });

  const navigate = useCallback(
    (offset: 1 | -1) => {
      setPagination((prev: PaginationShape): PaginationShape => {
        const { currentSlideType, currentSlideSubIndex } = prev;
        const slideTypeCount = slidesPerPageType.find(
          ({ key }) => key === currentSlideType
        )?.pageCount;

        if (!slideTypeCount) {
          diagnostics.info(
            `Could not find slide type count for ${currentSlideType}`
          );
          return prev;
        }

        const navOrder = getNavOrder(slidesPerPageType);

        // two nav cases:
        // case 1:  is the first/last slide
        if (
          currentSlideSubIndex + offset < 0 ||
          currentSlideSubIndex + offset >= slideTypeCount
        ) {
          const isFirst = currentSlideType === first(navOrder);
          const isLast = currentSlideType === last(navOrder);

          if ((offset === 1 && isLast) || (offset === -1 && isFirst)) {
            // this can be called by arrow key handling, so don't throw an error
            return prev;
          }

          const newTypeIndex = navOrder.indexOf(currentSlideType) + offset;
          const newSlideType = navOrder[newTypeIndex];
          if (!newSlideType) {
            diagnostics.error(
              `Could not navigate to slide at position ${newTypeIndex}-0`
            );
            return prev;
          }

          const payload = {
            currentSlideType: newSlideType,
            currentSlideSubIndex: 0,
          };

          return {
            ...payload,
            nextSlideEnabled: isNextSlideEnabled(payload, slidesPerPageType),
            previousSlideEnabled: isPreviousSlideEnabled(
              payload,
              slidesPerPageType
            ),
          };
        }

        // case 3: not the first/last slide
        const newSubIndex = currentSlideSubIndex + offset;
        const payload = {
          currentSlideType,
          currentSlideSubIndex: newSubIndex,
        };

        return {
          ...payload,
          nextSlideEnabled: isNextSlideEnabled(payload, slidesPerPageType),
          previousSlideEnabled: isPreviousSlideEnabled(
            payload,
            slidesPerPageType
          ),
        };
      });
    },
    [slidesPerPageType]
  );

  const nextSlide = useCallback(() => navigate(1), [navigate]);
  const previousSlide = useCallback(() => navigate(-1), [navigate]);

  const keyHandler = useCallback(
    (key?: string) => {
      if (key === 'ArrowRight') {
        navigate(1);
      } else if (key === 'ArrowLeft') {
        navigate(-1);
      }
    },
    [navigate]
  );

  useKeypress(keyHandler, ['ArrowRight', 'ArrowLeft']);

  return (
    <EntityPreviewModalPaginationContext.Provider
      value={{
        currentSlideType,
        currentSlideSubIndex,
        nextSlide,
        previousSlide,
        nextSlideEnabled,
        previousSlideEnabled,
        policyPages,
        numberOfPolicySlides,
        hasInsurance,
        slidesProps,
        numberOfBeneficiariesAndDispositionsSlides,
      }}
    >
      {children}
    </EntityPreviewModalPaginationContext.Provider>
  );
}
